/**
 * Reviews Service
 * Handles review and rating operations
 */

import api from './api';

class ReviewsService {
  /**
   * Get all reviews with filters
   * @param {Object} filters - Filter options
   */
  async getReviews(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.rating) params.append('rating', filters.rating);
    if (filters.driver) params.append('driver', filters.driver);
    if (filters.job) params.append('job', filters.job);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/review?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch reviews');
  }

  /**
   * Get review by ID
   * @param {string} reviewId - Review ID
   */
  async getReviewById(reviewId) {
    const response = await api.get(`/review/${reviewId}`);

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to fetch review');
  }

  /**
   * Get review statistics
   */
  async getStats() {
    const response = await api.get('/review/stats');

    if (response.success && response.data) {
      return response.data;
    }

    // Return mock data if endpoint doesn't exist
    return {
      averageRating: 4.5,
      totalReviews: 0,
      breakdown: {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0,
      },
    };
  }

  /**
   * Get driver reviews
   * @param {string} driverId - Driver ID
   * @param {Object} filters - Filter options
   */
  async getDriverReviews(driverId, filters = {}) {
    return this.getReviews({
      ...filters,
      driver: driverId,
    });
  }

  /**
   * Delete review (moderate)
   * @param {string} reviewId - Review ID
   */
  async deleteReview(reviewId) {
    const response = await api.delete(`/review/${reviewId}`);

    if (response.success) {
      return true;
    }

    throw new Error('Failed to delete review');
  }

  /**
   * Get driver ratings summary
   * @param {number} limit - Number of drivers to return
   */
  async getDriverRatings(limit = 10) {
    // Use the new top-drivers endpoint
    const response = await api.get(`/review/top-drivers?limit=${limit}`);

    if (response.success && response.data) {
      return response.data.drivers || response.data;
    }

    throw new Error('Failed to fetch driver ratings');
  }

  /**
   * Get flagged reviews for moderation queue
   * @param {Object} filters - Filter options
   */
  async getFlaggedReviews(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await api.get(`/review/flagged?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch flagged reviews');
  }

  /**
   * Flag a review as inappropriate
   * @param {string} reviewId - Review ID
   * @param {string} reason - Flag reason
   * @param {string} notes - Additional notes
   */
  async flagReview(reviewId, reason, notes = '') {
    const response = await api.post(`/review/${reviewId}/flag`, { reason, notes });

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to flag review');
  }

  /**
   * Unflag a review
   * @param {string} reviewId - Review ID
   */
  async unflagReview(reviewId) {
    const response = await api.post(`/review/${reviewId}/unflag`);

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to unflag review');
  }

  /**
   * Add admin response to a review
   * @param {string} reviewId - Review ID
   * @param {string} responseText - Admin response text
   */
  async addAdminResponse(reviewId, responseText) {
    const response = await api.post(`/review/${reviewId}/respond`, { response: responseText });

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to add response');
  }

  /**
   * Hide a review from public display
   * @param {string} reviewId - Review ID
   * @param {string} reason - Hide reason
   */
  async hideReview(reviewId, reason = '') {
    const response = await api.post(`/review/${reviewId}/hide`, { reason });

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to hide review');
  }

  /**
   * Unhide a review
   * @param {string} reviewId - Review ID
   */
  async unhideReview(reviewId) {
    const response = await api.post(`/review/${reviewId}/unhide`);

    if (response.success && response.data) {
      return response.data.review || response.data;
    }

    throw new Error('Failed to unhide review');
  }
}

const reviewsService = new ReviewsService();
export default reviewsService;
